import { buildValidations, validator } from 'ember-cp-validations';
import { computed } from '@ember/object';
import { isoDateFormat } from 'client-portal/utils/date-time';
import { reads } from '@ember/object/computed';
import { tracked } from '@glimmer/tracking';
import Model, { attr, belongsTo, hasMany } from '@ember-data/model';
import classic from 'ember-classic-decorator';

const Validations = buildValidations({
  insuredAddress: [
    validator('presence', {
      presence: true,
      disabled: reads('model.selfInsured'),
      descriptionKey: 'errors.insuranceInfoRequestConnection.insuredAddress',
      messageKey: 'errors.presence',
    }),
  ],
  insuredCity: [
    validator('presence', {
      presence: true,
      disabled: reads('model.selfInsured'),
      descriptionKey: 'errors.insuranceInfoRequestConnection.insuredCity',
      messageKey: 'errors.presence',
    }),
  ],
  insuredDob: [
    validator('presence', {
      presence: true,
      disabled: reads('model.selfInsured'),
      descriptionKey: 'errors.insuranceInfoRequestConnection.insuredDob',
      messageKey: 'errors.presence',
    }),
  ],
  insuredFname: [
    validator('presence', {
      presence: true,
      disabled: reads('model.selfInsured'),
      descriptionKey: 'errors.insuranceInfoRequestConnection.insuredFname',
      messageKey: 'errors.presence',
    }),
  ],
  insuredLname: [
    validator('presence', {
      presence: true,
      disabled: reads('model.selfInsured'),
      descriptionKey: 'errors.insuranceInfoRequestConnection.insuredLname',
      messageKey: 'errors.presence',
    }),
  ],
  insuredPhone: [
    validator('presence', {
      presence: true,
      disabled: reads('model.selfInsured'),
      descriptionKey: 'errors.insuranceInfoRequestConnection.insuredPhone',
      messageKey: 'errors.presence',
    }),
    validator('length', {
      is: 10,
      disabled: reads('model.selfInsured'),
      descriptionKey: 'errors.insuranceInfoRequestConnection.insuredPhone',
      messageKey: 'errors.length',
    }),
  ],
  insurancePlan: [
    validator('presence', {
      presence: true,
      descriptionKey: 'errors.insuranceInfoRequestConnection.insurancePlan',
      messageKey: 'errors.presence',
    }),
  ],
  insuredRelation: [
    validator('presence', {
      presence: true,
      descriptionKey: 'errors.insuranceInfoRequestConnection.insuredRelation',
      messageKey: 'errors.presence',
    }),
  ],
  insuredSex: [
    validator('presence', {
      presence: true,
      descriptionKey: 'errors.insuranceInfoRequestConnection.insuredSex',
      messageKey: 'errors.presence',
    }),
  ],
  insuredState: [
    validator('presence', {
      presence: true,
      disabled: reads('model.selfInsured'),
      descriptionKey: 'errors.insuranceInfoRequestConnection.insuredState',
      messageKey: 'errors.presence',
    }),
  ],
  insuredZip: [
    validator('presence', {
      presence: true,
      disabled: reads('model.selfInsured'),
      descriptionKey: 'errors.insuranceInfoRequestConnection.insuredZip',
      messageKey: 'errors.presence',
    }),
  ],
  memberId: [
    validator('presence', {
      presence: true,
      descriptionKey: 'errors.insuranceInfoRequestConnection.memberId',
      messageKey: 'errors.presence',
    }),
  ],
});

@classic
export default class InsuranceInfoRequestConnectionModel extends Model.extend(Validations) {
  @belongsTo('treatable-client', { polymorphic: true, async: false }) client;
  @belongsTo('insurance-provider-name', { async: false, inverse: null }) insurancePlan;
  @belongsTo('clinician-insurance-info-review', { async: false }) clinicianInsuranceInfoReview;
  @belongsTo('document-request-insurance-info', { async: false }) documentRequestInsuranceInfo;
  @hasMany('document', { async: false, inverse: null }) documents;

  @attr() thisType;
  @attr() memberId;
  @attr() planId;
  @attr() groupId;
  @attr() insuredRelation;
  @attr() insuredFname;
  @attr() insuredMname;
  @attr() insuredLname;
  @attr('moment', { format: isoDateFormat }) insuredDob;
  @attr() insuredPhone;
  @attr() insuredAddress;
  @attr() insuredCity;
  @attr() insuredState;
  @attr() insuredZip;
  @attr() insuredSex;
  @attr() createdAt;
  @attr('moment', { format: isoDateFormat }) effectiveStartDate;
  @attr('moment', { format: isoDateFormat }) effectiveEndDate;

  @tracked isDirty = false;

  @computed('insuredRelation')
  get selfInsured() {
    return this.insuredRelation === '0';
  }

  assignMappedFields(insuranceTextExtraction) {
    if (!insuranceTextExtraction) return;
    this.assignField(
      'memberId',
      insuranceTextExtraction.memberId,
      insuranceTextExtraction.memberIdConfidenceScore
    );
    this.assignField(
      'planId',
      insuranceTextExtraction.planId,
      insuranceTextExtraction.planIdConfidenceScore
    );
    this.assignField(
      'groupId',
      insuranceTextExtraction.groupId,
      insuranceTextExtraction.groupIdConfidenceScore
    );
  }

  assignField(field, value, confidenceScore) {
    if (value && confidenceScore >= 90) {
      this[field] = value;
    } else {
      this[field] = null;
    }
  }

  get extractionFieldsPresent() {
    return this.memberId || this.planId || this.groupId;
  }
}
